import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useApplications, type Application } from '@/hooks/useApplications'
import { useWorkspaceStore } from '@/store/workspace'
import { Search, Sparkles, Filter, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react'
import { db } from '@/lib/firebase'
import { collection, query, where, getDocs } from 'firebase/firestore'

const STATUSES: Array<Application['status'] | 'all'> = [
    'all', 'new', 'reviewed', 'shortlisted', 'interviewing', 'offered', 'hired', 'rejected'
]

function resolveIngestApiBase() {
    const rawUrl = (import.meta.env.VITE_HRX_INGEST_URL || 'https://ingest.hrx.nexx.ai/v1').trim()
    let normalized = rawUrl.replace(/\/+$/, '')
    if (!/^https?:\/\//i.test(normalized)) {
        normalized = `https://${normalized}`
    }
    return normalized.endsWith('/v1') ? normalized : `${normalized}/v1`
}

export default function Applications() {
    const navigate = useNavigate()
    const { apps, loading } = useApplications()
    const { workspaceId } = useWorkspaceStore()
    const envApiKey = (import.meta.env.VITE_HRX_API_KEY || '').trim()
    const [search, setSearch] = useState('')
    const [statusF, setStatusF] = useState<typeof STATUSES[number]>('all')
    const [selectedIds, setSelectedIds] = useState<string[]>([])
    const [isProcessing, setIsProcessing] = useState(false)
    const [apiKey, setApiKey] = useState<string | null>(envApiKey || null)
    const [error, setError] = useState<string | null>(null)

    // Fetch API key for the workspace to call worker
    useEffect(() => {
        if (envApiKey) {
            setApiKey(envApiKey)
            return
        }
        if (!workspaceId) return
        const fetchApiKey = async () => {
            try {
                const q = query(collection(db, 'apiKeys'), where('workspaceId', '==', workspaceId))
                const snap = await getDocs(q)
                if (!snap.empty) {
                    const data = snap.docs[0].data()
                    if (typeof data.key === 'string' && data.key.trim().length > 0) {
                        setApiKey(data.key.trim())
                    } else {
                        setApiKey(null)
                    }
                } else {
                    setApiKey(null)
                }
            } catch {
                setApiKey(null)
            }
        }
        fetchApiKey()
    }, [workspaceId, envApiKey])

    const filtered = apps
        .filter(a => statusF === 'all' || a.status === statusF)
        .filter(a => {
            const q = search.toLowerCase()
            return (
                a.name.toLowerCase().includes(q) ||
                a.email.toLowerCase().includes(q) ||
                a.position.toLowerCase().includes(q)
            )
        })

    const toggleSelect = (id: string, e: React.MouseEvent) => {
        e.stopPropagation()
        setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
    }

    const toggleSelectAll = () => {
        if (selectedIds.length === filtered.length) setSelectedIds([])
        else setSelectedIds(filtered.map(a => a.id))
    }

    const handleProcessAi = async () => {
        if (!apiKey) {
            setError('Missing HRX API key. Set VITE_HRX_API_KEY in Cloudflare Pages environment variables and redeploy.')
            return
        }
        if (selectedIds.length === 0) return
        setIsProcessing(true)
        setError(null)

        try {
            const ingestBase = resolveIngestApiBase()
            const res = await fetch(`${ingestBase}/process-ai`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-HRX-Key': apiKey
                },
                body: JSON.stringify({ appIds: selectedIds })
            })
            const contentType = res.headers.get('content-type') || ''
            const isJson = contentType.includes('application/json')
            const data = isJson ? await res.json() : null

            if (!res.ok) {
                throw new Error(data?.error || `Failed to trigger AI (${res.status})`)
            }

            if (!isJson) {
                throw new Error('Worker returned non-JSON response. If this is a workers.dev URL, disable Cloudflare Access for API routes or use a public ingest domain.')
            }

            if (!data?.success) {
                throw new Error(data?.error || 'Failed to trigger AI')
            }

            // Clear selection on success
            setSelectedIds([])
            alert(`✅ ${data.message || 'Queued for processing'}`)
        } catch (err: any) {
            const message = err?.message || 'Failed to trigger AI'
            if (/Failed to fetch/i.test(message)) {
                setError('Network/CORS issue. Set VITE_HRX_INGEST_URL to your Worker endpoint (e.g. https://hrx-ingest-prod...workers.dev/v1) and allow your Pages origin in Worker CORS_ORIGIN.')
            } else {
                setError(message)
            }
            console.error(err)
        } finally {
            setIsProcessing(false)
        }
    }

    return (
        <div>
            <div className="page-header" style={{ marginBottom: 24 }}>
                <div>
                    <h1 className="page-title">Applications</h1>
                    <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{apps.length} total</div>
                </div>

                {selectedIds.length > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 16px', background: 'var(--card-bg)', border: '1px solid var(--accent)', borderRadius: 12, animation: 'slideIn 0.2s ease-out' }}>
                        <span style={{ fontSize: 13, fontWeight: 600 }}>{selectedIds.length} selected</span>
                        <div style={{ width: 1, height: 16, background: 'var(--border)' }} />
                        <button className="btn btn-primary btn-sm" onClick={handleProcessAi} disabled={isProcessing} style={{ gap: 6 }}>
                            {isProcessing ? <Loader2 size={14} className="spinner" /> : <Sparkles size={14} />}
                            Process with AI
                        </button>
                    </div>
                )}
            </div>

            {error && (
                <div style={{ padding: 12, marginBottom: 20, borderRadius: 8, background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.4)', color: '#f87171', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                    <AlertCircle size={14} /> {error}
                </div>
            )}

            {/* Filters */}
            <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
                <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
                    <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-dim)' }} />
                    <input className="input" placeholder="Search name, email, position…"
                        style={{ paddingLeft: 36 }} value={search} onChange={e => setSearch(e.target.value)} />
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {STATUSES.map(s => (
                        <button key={s} onClick={() => setStatusF(s)}
                            className="btn btn-ghost btn-sm"
                            style={{ background: statusF === s ? 'var(--accent-glow)' : undefined, color: statusF === s ? 'var(--accent)' : undefined, border: statusF === s ? '1px solid rgba(108,71,255,0.4)' : undefined }}>
                            {s.charAt(0).toUpperCase() + s.slice(1)}
                        </button>
                    ))}
                </div>
            </div>

            {/* Table */}
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                {loading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><div className="spinner" /></div>
                ) : filtered.length === 0 ? (
                    <div className="empty-state">
                        <Filter size={32} style={{ opacity: 0.3 }} />
                        <div>No applications match your filter</div>
                    </div>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table className="table">
                            <thead>
                                <tr>
                                    <th style={{ width: 40 }}>
                                        <input type="checkbox" checked={selectedIds.length === filtered.length && filtered.length > 0} onChange={toggleSelectAll} />
                                    </th>
                                    <th>Candidate</th>
                                    <th>Position</th>
                                    <th>AI Score</th>
                                    <th>Status</th>
                                    <th>Applied</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map(app => (
                                    <tr key={app.id} onClick={() => navigate(`/applications/${app.id}`)} style={{ cursor: 'pointer', background: selectedIds.includes(app.id) ? 'rgba(108, 71, 255, 0.05)' : undefined }}>
                                        <td onClick={(e) => toggleSelect(app.id, e as any)}>
                                            <input type="checkbox" checked={selectedIds.includes(app.id)} readOnly />
                                        </td>
                                        <td>
                                            <div style={{ fontWeight: 600 }}>{app.name}</div>
                                            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{app.email}</div>
                                        </td>
                                        <td style={{ color: 'var(--text-muted)', fontSize: 13 }}>{app.position}</td>
                                        <td>
                                            {app.aiScore != null ? (
                                                <span className={`ai-score ${app.aiScore >= 70 ? 'ai-score-high' : app.aiScore >= 40 ? 'ai-score-mid' : 'ai-score-low'}`}>
                                                    <Sparkles size={10} /> {app.aiScore}
                                                </span>
                                            ) : app.aiProcessed ? (
                                                <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>Failed</span>
                                            ) : (
                                                <span style={{ fontSize: 12, color: 'var(--text-dim)', display: 'flex', alignItems: 'center', gap: 4 }}>
                                                    <Loader2 size={12} className={isProcessing && selectedIds.includes(app.id) ? 'spinner' : ''} />
                                                    Pending AI
                                                </span>
                                            )}
                                        </td>
                                        <td><span className={`badge badge-${app.status}`}>{app.status}</span></td>
                                        <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                                            {new Date(app.appliedAt).toLocaleDateString('en-IN')}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    )
}
